import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CircleCheck,
  CircleSlash,
  Eye,
  Mail,
  MailCheck,
  MailPlus,
  Pencil,
  Plus,
  Send,
  Users as UsersIcon,
} from 'lucide-react'
import Button from '../../components/Button'
import { useOnChange, useRemembered } from '../../components/listMemory'
import { usePageParam } from '../../components/useListParams'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import NotificationsModal from '../../components/NotificationsModal'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import StatStrip from '../../components/StatStrip'
import Select from '../../components/Select'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import InvitationFormDrawer from './InvitationFormDrawer'
import {
  INVITATION_STATUS,
  changeInvitationStatus,
  countInvitations,
  getInvitationsPage,
  listInvitations,
  resendInvitationEmail,
  saveBulkInvitations,
  saveInvitation,
} from './invitationsService'
import { strings as S } from './strings'
import './InvitationsPage.css'
import { serverDetail, serverText } from '../../lib/i18n'

const STATUS_OPTIONS = [
  { value: String(INVITATION_STATUS.SENT), label: S.status[INVITATION_STATUS.SENT] },
  { value: String(INVITATION_STATUS.CANCELED), label: S.status[INVITATION_STATUS.CANCELED] },
  { value: String(INVITATION_STATUS.USER_REGISTERED), label: S.status[INVITATION_STATUS.USER_REGISTERED] },
]

const ADMIN_OPTIONS = [
  { value: 'false', label: S.filter.no },
  { value: 'true', label: S.filter.yes },
]

const STATUS_TONE = { 0: 'primary', 1: 'success', 2: 'danger' }

/** Both filters off — the state the screen opens on and the one `إلغاء` returns it to. */
const EMPTY_FILTER = { status: '', isAdmin: '' }

/**
 * الدعوات — Views/Invitations/Index.cshtml + js.cshtml.
 *
 * The legacy table is unsortable (`ordering:false`) and fixed to CreatedOn DESC; the
 * handler now orders by the column the table names, opening on that same default.
 * Row actions are status-gated
 * exactly as `js.cshtml:135-152` derives them; cancel and activate fire without a
 * confirmation, which is also what the legacy does.
 */
export default function InvitationsPage() {
  const toast = useToast()
  /* What the remembered page is a page OF. Both were plain state and were dropped on the
     way out, so opening an invitation and coming back landed on page 4 of every
     invitation rather than of the cancelled ones being read. */
  const [query, setQuery] = useRemembered('invitations:query', '')
  const [filter, setFilter] = useRemembered('invitations:filter', EMPTY_FILTER)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one — without this the table renders its "no
     records" state for a server error, which is the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [formMode, setFormMode] = useState(null)
  const [editing, setEditing] = useState(null)
  const [resending, setResending] = useState(null)
  const [notificationsFor, setNotificationsFor] = useState(null)

  /** The list is paged, searched and sorted by the server; this is what it is told. */
  /* The page is in the address; the size and the sort are the tab's own. */
  const [page, setPage] = usePageParam()
  const [tableState, setTableState] = useRemembered('invitations:table', { pageSize: 10, sort: { key: 'createdOn', dir: 'desc' } })
  const listState = useMemo(
    () => ({ page, pageSize: tableState.pageSize, sort: tableState.sort }),
    [page, tableState],
  )
  const setListState = useCallback(
    (next) => {
      const applied = typeof next === 'function' ? next(listState) : next
      setPage(applied.page)
      setTableState({ pageSize: applied.pageSize, sort: applied.sort })
    },
    [listState, setPage, setTableState],
  )
  const [total, setTotal] = useState(0)
  /** Bumped by every action so the table and the strip re-read together. */
  const [reloadKey, setReloadKey] = useState(0)
  const [groups, setGroups] = useState([])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listInvitations({
      status: filter.status === '' ? null : Number(filter.status),
      isAdmin: filter.isAdmin === '' ? null : filter.isAdmin === 'true',
      search: query,
      ...listState,
    })
      .then((response) => {
        if (cancelled) return
        setLoadError(null)
        setRows(response.rows)
        setTotal(response.total)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setRows([])
        setTotal(0)
        setLoading(false)
        /* An empty table that will not say why is worse than an error. The server's own
           reason goes on screen — a refused role, an expired session, a 404 from an API
           that has not been restarted all look identical otherwise. */
        toast.error(serverDetail(error?.message) || S.fail.list)
      })
    return () => {
      cancelled = true
    }
  }, [filter, listState, query, reloadKey, toast])

  /* Narrowing the list starts it again at the first page — the address kept a page the
     filtered list no longer has otherwise. The SCREEN does this rather than DataTable's
     `resetKey`: the endpoint pages, so the page number is this component's, not the
     table's. On a CHANGE only, or arriving would discard the page just restored. */
  useOnChange(() => setPage(1), [query, filter])

  useEffect(() => {
    getInvitationsPage()
      .then(({ groups: list }) => setGroups(list))
      .catch((error) => {
        setGroups([])
        /* Without this the department select is simply empty, which reads as "there are no
           departments" rather than "this call failed". */
        toast.error(serverDetail(error?.message) || S.fail.groups)
      })
  }, [toast])

  /* The list itself is filtered on the server, so `rows` only ever holds the
     current slice and cannot be counted for a summary. The strip reads an
     unfiltered fetch of its own — the same call with no arguments — so the
     figures stay put while the list below them narrows. */
  const [totals, setTotals] = useState(null)

  useEffect(() => {
    countInvitations()
      .then(setTotals)
      // The table above already reported the reason; this only stops the strip guessing.
      .catch(() => setTotals(null))
  }, [reloadKey])

  const openNotifications = useCallback((row) => setNotificationsFor(row), [])

  const openEdit = useCallback((row) => {
    setEditing(row)
    setFormMode('single')
  }, [])

  /**
   * Cancel and re-activate are the same call with a different status.
   *
   * This used to edit the row in local state and stop there — the invitation was never
   * cancelled, and a refresh brought it back looking active.
   */
  const setStatus = useCallback(
    async (row, status) => {
      const outcome = await changeInvitationStatus(row.id, status)
      if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.status)
      toast.success(serverText(outcome.message) || S.actionDone)
      setReloadKey((key) => key + 1)
    },
    [toast],
  )

  const columns = useMemo(
    () => [
      {
        key: 'email',
        header: S.columns.email,
        render: (row) => (
          <span className="invitations__mail" dir="ltr">
            {row.email}
          </span>
        ),
      },
      {
        key: 'groupName',
        header: S.columns.group,
        render: (row) => <bdi>{row.groupName ?? ''}</bdi>,
      },
      {
        key: 'createdOn',
        header: S.columns.createdOn,
        render: (row) => (
          <span className="invitations__date" dir="ltr">
            {row.createdOnText}
          </span>
        ),
      },
      {
        key: 'expiryDate',
        header: S.columns.expiryDate,
        render: (row) => (
          <span className="invitations__date" dir="ltr">
            {row.expiryDateText}
          </span>
        ),
      },
      {
        key: 'status',
        header: S.columns.status,
        value: (row) => S.status[row.status] ?? '',
        render: (row) => (
          <StatusBadge tone={STATUS_TONE[row.status] ?? 'dark'}>{S.status[row.status]}</StatusBadge>
        ),
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        /* The set depends on the invitation's state, so a row can carry two
           actions or four; RowActions renders three or fewer whole and only
           reaches for a kebab past that. Cancel and activate fire straight
           through — the legacy asks for no confirmation on either. */
        render: (row) => (
          <RowActions
            actions={[
              {
                id: 'notifications',
                icon: Eye,
                label: S.row.notifications,
                tone: 'info',
                onClick: () => openNotifications(row),
              },
              { id: 'edit', icon: Pencil, label: S.row.edit, onClick: () => openEdit(row) },
              row.status !== INVITATION_STATUS.USER_REGISTERED && {
                id: 'resend',
                icon: Mail,
                label: S.row.resend,
                /* Green whatever the invitation's state. It used to turn red once the
                   invitation had lapsed, which painted the one action that FIXES that
                   state in the colour this app keeps for what cannot be undone. The
                   state is already told by the row's own status chip. */
                tone: 'success',
                onClick: () => setResending(row),
              },
              row.status === INVITATION_STATUS.SENT && {
                /* The slashed circle is the same mark the `ملغاة` tile carries above the
                   table: an action drawn as the state it produces. An X here was the
                   dismiss glyph again, on the one entry that ends the invitation. */
                id: 'cancel',
                icon: CircleSlash,
                label: S.row.cancel,
                danger: true,
                onClick: () => setStatus(row, INVITATION_STATUS.CANCELED),
              },
              row.status === INVITATION_STATUS.CANCELED && {
                id: 'activate',
                icon: CircleCheck,
                label: S.row.activate,
                tone: 'success',
                onClick: () => setStatus(row, INVITATION_STATUS.SENT),
              },
            ]}
          />
        ),
      },
    ],
    [openEdit, openNotifications, setStatus],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        count={loading ? undefined : rows.length}
        actions={
          <>
            <Button
              onClick={() => {
                setEditing(null)
                setFormMode('single')
              }}
            >
              <Plus size={16} strokeWidth={2} />
              {S.actions.newInvitation}
            </Button>
            <Button
              variant="soft"
              onClick={() => {
                setEditing(null)
                setFormMode('bulk')
              }}
            >
              <UsersIcon size={16} strokeWidth={1.75} />
              {S.actions.manyInvitations}
            </Button>
          </>
        }
      />

      {/* The cards ARE the status filter, writing the same `filter.status` the
          select below them does, so the two never disagree. */}
      <StatStrip
        label={S.stats.caption}
        activeId={filter.status === '' ? 'all' : filter.status}
        onSelect={(id) => {
          /* ALL IS NOT A STATUS, it is the absence of one - it clears the filter rather
             than writing itself into it. The three cards beside it toggle. */
          if (id === 'all') return setFilter((previous) => ({ ...previous, status: '' }))
          setFilter((previous) => ({ ...previous, status: previous.status === id ? '' : id }))
        }}
        items={[
          {
            /* Was an unclickable figure labelled "Total invitations": the only way back to
               every status was to click the selected card a second time, or to clear the
               chip. The figures come from an UNFILTERED count, so this one is the true
               total of the status dimension whatever is selected. */
            id: 'all',
            icon: MailPlus,
            label: S.stats.all,
            value: totals?.total ?? 0,
            tone: 'violet',
          },
          { id: String(INVITATION_STATUS.SENT), icon: Send, label: S.status[INVITATION_STATUS.SENT], value: totals?.[INVITATION_STATUS.SENT] ?? 0, tone: 'info' },
          {
            id: String(INVITATION_STATUS.USER_REGISTERED),
            icon: MailCheck,
            label: S.status[INVITATION_STATUS.USER_REGISTERED],
            value: totals?.[INVITATION_STATUS.USER_REGISTERED] ?? 0,
            tone: 'success',
          },
          {
            id: String(INVITATION_STATUS.CANCELED),
            icon: CircleSlash,
            label: S.status[INVITATION_STATUS.CANCELED],
            value: totals?.[INVITATION_STATUS.CANCELED] ?? 0,
            tone: 'danger',
          },
        ]}
      />

      <FilterBar
        search={{ value: query, onChange: setQuery, placeholder: S.filter.trigger }}
        chips={[
          filter.status !== '' && {
            id: 'status',
            label: S.filter.statusPlaceholder,
            value: STATUS_OPTIONS.find((option) => String(option.value) === String(filter.status))?.label ?? '',
            onRemove: () => setFilter((previous) => ({ ...previous, status: '' })),
          },
          filter.isAdmin !== '' && {
            id: 'isAdmin',
            label: S.filter.adminPlaceholder,
            value: ADMIN_OPTIONS.find((option) => String(option.value) === String(filter.isAdmin))?.label ?? '',
            onRemove: () => setFilter((previous) => ({ ...previous, isAdmin: '' })),
          },
        ].filter(Boolean)}
        resetLabel={S.filter.reset}
        onReset={() => setFilter(EMPTY_FILTER)}
      >
        <Select
          value={filter.status}
          onChange={(status) => setFilter((previous) => ({ ...previous, status }))}
          options={STATUS_OPTIONS}
          placeholder={S.filter.statusPlaceholder}
          aria-label={S.filter.statusLabel}
        />
        <Select
          value={filter.isAdmin}
          onChange={(isAdmin) => setFilter((previous) => ({ ...previous, isAdmin }))}
          options={ADMIN_OPTIONS}
          placeholder={S.filter.adminPlaceholder}
          aria-label={S.filter.adminLabel}
        />
      </FilterBar>

      <DataTable
        error={loadError}
        columns={columns}
        rows={rows}
        loading={loading}
        query={query}
        controls={false}
        getRowId={(row) => row.id}
        /* Paged, searched and sorted by the endpoint — `rows` is one page, so the table
           must not filter it again or the count under it will disagree with the strip. */
        server={{
          total,
          page: listState.page,
          pageSize: listState.pageSize,
          sort: listState.sort,
          onChange: ({ page, pageSize, sort }) => setListState({ page, pageSize, sort }),
        }}
      />

      <InvitationFormDrawer
        open={!!formMode}
        mode={formMode}
        invitation={editing}
        groups={groups}
        onClose={() => setFormMode(null)}
        /* Both forms used to close and toast success without saving anything. A failure
           now keeps the drawer open with the server's reason — which is where it says the
           address is already invited. */
        onSubmit={async (values) => {
          const outcome = values.excel
            ? await saveBulkInvitations({ file: values.excel, expiryDate: values.expiryDate })
            : await saveInvitation({ ...values, id: editing?.id })

          if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.save)

          setFormMode(null)
          /* A file of 200 addresses that produced 40 invitations is not a clean success,
             so the count comes with the message. */
          toast.success(
            values.excel && outcome.successCount
              ? `${outcome.message || S.actionDone} (${outcome.successCount})`
              : outcome.message || S.actionDone,
          )
          setReloadKey((key) => key + 1)
        }}
      />

      <ConfirmDialog
        open={!!resending}
        title={S.resendTitle}
        tone="primary"
        message={`${S.resendConfirm}
${resending?.email ?? ''}`}
        confirmLabel={S.yes}
        cancelLabel={S.no}
        onConfirm={async () => {
          const outcome = await resendInvitationEmail(resending.id)
          if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.resend)
          setResending(null)
          toast.success(serverText(outcome.message) || S.actionDone)
        }}
        onCancel={() => setResending(null)}
      />

      <NotificationsModal
        open={!!notificationsFor}
        objectId={notificationsFor?.id}
        templateTypes={[11]}
        onClose={() => setNotificationsFor(null)}
      />

    </>
  )
}
